import { useRef } from 'react'

import { IconSpin } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useLoaderData, useOutletContext } from 'react-router'

import { PortalLandingPageLoader, loader as contentLoader } from './loader'
import { EditContentButtons } from '~/portal/components/ConsumerEditContentButtons'
import { ConsumerFooter } from '~/portal/components/ConsumerFooter'
import { HeroSection } from '~/portal/components/ConsumerHeroSection'
import { LegacyHeroSection } from '~/portal/components/ConsumerLegacyHeroSection'
import EmptyLandingPage from '~/portal/components/EmptyLandingPage'
import { ProductTile } from '~/portal/components/ProductTile'
import { useConsumerBranding } from '~/portal/hooks/useConsumerBranding'
import { useFeatureFlag } from '~/portal/hooks/useFeatureFlag.ts'
import { useLandingPage } from '~/portal/hooks/useLandingPage'
import { useUser } from '~/portal/hooks/useUser'
import { PortalRouteHandle } from '~/portal/types/app'
import { FeatureFlags } from '~/portal/types/flags.ts'
import { LayoutType } from '~/portal/types/layout'
import { RootOutletContext } from '~/portal/types/portal'
import { consumerMeta } from '~/portal/utils/shared/meta'
import { getFullName } from '~/portal/utils/shared/user'
import { useDarkMode } from '~/shared/components/DarkMode/hook'
import { ErrorBoundaryComponent } from '~/shared/components/ErrorBoundary'
import { useInfiniteScroll } from '~/shared/hooks/useInfiniteScroll.ts'
import { useShubOrgInfo } from '~/shared/hooks/useShubOrgInfo'
import { isAtLeastDesigner } from '~/shared/utils/roles'

export const loader = contentLoader

export const handle: PortalRouteHandle = {
  layout: {
    [LayoutType.DEFAULT]: {
      consumerTopBar: {
        search: {
          disabled: false
        }
      }
    }
  }
}

export const meta = consumerMeta

const LOADER_HREF = '?index'

export default function DocsOrgLandingPage() {
  const { isDark } = useDarkMode()

  const landingPageData = useLandingPage()
  const { isPreview } = useOutletContext<RootOutletContext>()

  const { accentColor, description, landingPageContent, heroImage, illustrationImage, fontName, name, footer } = useConsumerBranding()

  const htmlContentEnabled = useFeatureFlag(FeatureFlags.LANDING_PAGE_CK_EDITOR)

  const { products, productsPage } = useLoaderData<PortalLandingPageLoader>()

  const showFooter = footer?.visible

  const { organizationRole } = useShubOrgInfo()

  const { user } = useUser()

  const infiniteScrollContainerRef = useRef<HTMLDivElement>(null)

  const { data, isFetching, fetchMore } = useInfiniteScroll(
    LOADER_HREF,
    products,
    productsPage?.totalElements ?? 0,
    infiniteScrollContainerRef.current,
  )

  const userFullName = getFullName(user)
  
  const hasHeroContent = htmlContentEnabled
    ? Boolean(landingPageContent?.trim())
    : Boolean(description?.length) || Boolean(heroImage) || Boolean(illustrationImage)

  const isEmptyPage = !isFetching
    && Boolean(!data?.length)
    && !hasHeroContent
  
  if (isEmptyPage) {
    return (
      <EmptyLandingPage role={organizationRole} isDark={isDark()} portalName={name} />
    )
  }

  return (
    <div className="sp-landing-page relative flex flex-col flex-1 overflow-auto overflow-x-hidden bg-(--sp-color-surface) dark:bg-(--sp-color-surface-sunken)" ref={infiniteScrollContainerRef} onScroll={({ target }) => fetchMore(target as HTMLDivElement)}>
      {isAtLeastDesigner(organizationRole) && (
        <div className="absolute right-0 flex justify-end p-6 w-fit">
          <EditContentButtons contentUrl="/sp-admin/settings/general" />
        </div>
      )}
      {htmlContentEnabled
        ? <HeroSection content={landingPageContent} fontFamily={fontName} />
        : <LegacyHeroSection
          heroImage={heroImage}
          illustrationImage={illustrationImage}
          pageDescription={description}
          fontFamily={fontName}
        />
      }

      <section className="sp-landing-page-bg flex-1 bg-(--sp-color-surface-sunken) dark:bg-(--sp-color-surface)">
        <div className={clsx(
          'sp-product-grid max-w-[80rem] grid lg:grid-cols-2 xl:grid-cols-3 px-6 md:px-9 xl:px-[3.75rem] mx-auto gap-y-6 md:gap-y-9 lg:gap-x-9',
          showFooter ? 'pt-6 md:pt-9 xl:pt-12 pb-12' : 'py-6 md:py-9 xl:py-12'
        )}>
          {Boolean(data?.length) && data.map(product =>
            <ProductTile
              key={product.id}
              accentColor={accentColor}
              fontFamily={fontName}
              product={product}
              isPreview={isPreview}
              href={`/${product.slug}`}
              loggedIn={!!user?.email}
              portalName={landingPageData.name}
              userFullName={userFullName}
              responsive={true}
            />
          )}
        </div>
        {isFetching && <div className="flex items-center justify-center pb-20">
          <IconSpin width={48} height={48} className="mx-auto text-(--sp-color-link) dark:text-(--sp-color-link)" />
        </div>}
      </section>
      {showFooter && <ConsumerFooter footer={footer} isLandingPage={true} />}
    </div>
  )
}

export const ErrorBoundary = ErrorBoundaryComponent